import React, { useEffect, useState } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Autocomplete,
	TextField,
	CircularProgress,
	Box,
	LinearProgress,
	Typography,
} from "@mui/material";
import { API } from "../../queries/api";
import useDebounce from "../../hooks/useDebounce";
import { UserType } from "../../types";
import useSnackbar from "../../hooks/useSnackbar";
import { matrixClient } from "../../services/matrixClient";

/**
 * AddUserToChatModalMatrix — Matrix version of AddUserToChatModal
 *
 * Supports both direct Synapse mode and backend proxy mode.
 * In direct mode, invites users directly via Matrix API.
 */

interface AddUserToChatModalMatrixProps {
	open: boolean;
	onClose: () => void;
	chatId: string;
	clubId: string;
	onSuccess?: () => void;
}

const AddUserToChatModalMatrix: React.FC<AddUserToChatModalMatrixProps> = ({
	open,
	onClose,
	chatId,
	clubId,
	onSuccess,
}) => {
	const [searchQuery, setSearchQuery] = useState<string>("");
	const [searchResults, setSearchResults] = useState<UserType[]>([]);
	const [selectedUsers, setSelectedUsers] = useState<UserType[]>([]);
	const [isUserSearchLoading, setIsUserSearchLoading] = useState(false);
	const [isAdding, setIsAdding] = useState(false);
	const [isUsersTypeaheadOpen, setIsUsersTypeaheadOpen] = useState(false);
	const [directUserId, setDirectUserId] = useState("");
	const openSnackbar = useSnackbar();

	const debouncedSearchTerm = useDebounce(searchQuery, 500);

	useEffect(() => {
		if (debouncedSearchTerm.trim().length > 0) {
			searchUsers(debouncedSearchTerm);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedSearchTerm]);

	const searchUsers = async (query: string) => {
		try {
			setIsUserSearchLoading(true);
			const payload = {
				startDate: "2020-10-03T20:42:30.519Z",
				endDate: "2029-10-03T20:42:30.519Z",
				page: 0,
				size: 20,
				reverse: true,
			};
			const { data, status } = await API.post(
				`users/search?searchStr=${query}&idClub=${clubId}`,
				payload
			);
			const users = data?.items;
			if (status === 200 && !!users.length) {
				setSearchResults(data?.items);
			} else {
				setSearchResults([]);
			}
		} catch (error) {
			console.log(error);
			setSearchResults([]);
		}
		setIsUserSearchLoading(false);
	};

	const handleAddUsers = async () => {
		try {
			setIsAdding(true);
			let addedCount = 0;

			// Add users from search selection
			for (const user of selectedUsers) {
				try {
					await matrixClient.addMember(chatId, user.id);
					addedCount++;
				} catch (err) {
					console.error(`Failed to add user ${user.id}:`, err);
				}
			}

			// Add direct Matrix user ID if provided
			if (directUserId.trim()) {
				try {
					await matrixClient.addMember(chatId, directUserId.trim());
					addedCount++;
				} catch (err) {
					console.error(`Failed to add user ${directUserId}:`, err);
				}
			}

			if (addedCount > 0) {
				openSnackbar(`Successfully added ${addedCount} user(s) to chat`);
			} else {
				openSnackbar("No users were added");
			}

			setSelectedUsers([]);
			setSearchQuery("");
			setSearchResults([]);
			setDirectUserId("");

			if (onSuccess) onSuccess();
			onClose();
		} catch (error) {
			console.error("Error adding users:", error);
			openSnackbar("Error adding users to chat");
		}
		setIsAdding(false);
	};

	const handleClose = () => {
		if (!isAdding) {
			setSelectedUsers([]);
			setSearchQuery("");
			setSearchResults([]);
			setDirectUserId("");
			onClose();
		}
	};

	React.useEffect(() => {
		if (!isUsersTypeaheadOpen) {
			setSearchResults([]);
		}
	}, [isUsersTypeaheadOpen]);

	const isDirectMode = matrixClient.mode === "direct";

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Add Users to Chat (Matrix)</DialogTitle>
			<DialogContent>
				{isAdding && (
					<Box sx={{ width: "100%", mb: 2 }}>
						<LinearProgress />
					</Box>
				)}

				{/* Direct Matrix user ID input (for direct Synapse mode) */}
				{isDirectMode && (
					<Box sx={{ mb: 2 }}>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Add by Matrix User ID
						</Typography>
						<TextField
							fullWidth
							size="small"
							placeholder="@username:server or just username"
							value={directUserId}
							onChange={(e) => setDirectUserId(e.target.value)}
							disabled={isAdding}
							helperText="Enter Matrix user ID (e.g., @username:server)"
						/>
					</Box>
				)}

				{/* Search from backend user directory */}
				<Typography variant="subtitle2" sx={{ mb: 1 }}>
					Search Users
				</Typography>
				<Autocomplete
					multiple
					sx={{ mt: 1 }}
					open={isUsersTypeaheadOpen}
					onOpen={() => setIsUsersTypeaheadOpen(true)}
					onClose={() => setIsUsersTypeaheadOpen(false)}
					isOptionEqualToValue={(option, value) => option.id === value.id}
					getOptionLabel={(option) =>
						`${option.firstName} ${option.lastName} (${option.email})`
					}
					options={searchResults}
					loading={isUserSearchLoading}
					value={selectedUsers}
					onChange={(_event, newValue) => setSelectedUsers(newValue)}
					disabled={isAdding}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Search Users"
							value={searchQuery}
							onChange={(event) => setSearchQuery(event.target.value)}
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<React.Fragment>
										{isUserSearchLoading ? (
											<CircularProgress color="inherit" size={20} />
										) : null}
										{params.InputProps.endAdornment}
									</React.Fragment>
								),
							}}
						/>
					)}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={isAdding}>
					Cancel
				</Button>
				<Button
					onClick={handleAddUsers}
					variant="contained"
					color="primary"
					disabled={isAdding || (selectedUsers.length === 0 && !directUserId.trim())}
				>
					Add Users
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AddUserToChatModalMatrix;
